import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { getPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";
import { DateRange } from "../../types/DataRange";
import { DateFns, ConstantFormat } from "../../utils/DateFns";

/**
 * Отчёт по заказам исполнителя: успешные заказы по исполнителям
 * с разбивкой по месяцам за период. Фильтры — филиалы, исполнители,
 * автопарки и автомобили (парковые номера).
 */
class ExecutorOrdersReport {
	static async getReport(
		params: ExecutorOrdersReport.SearchOptions,
	): Promise<ExecutorOrdersReport.Response | null> {
		const prpc = getPRPC();
		if (!prpc?.theirsModel?.report?.getExecutorOrdersReport) return null;

		const payload: Record<string, unknown> = {
			dateRange: params.dateRange,
		};
		// prpc передаёт ключи со значением undefined, а gateway отвечает
		// Joi-ошибкой на неизвестные ключи — добавляем только заданные.
		if (params.taxiServiceIds?.length)
			payload.taxiServiceIds = params.taxiServiceIds;
		if (params.fleetIds?.length) payload.fleetIds = params.fleetIds;
		if (params.executorIds?.length)
			payload.executorIds = params.executorIds;
		if (params.carIds?.length) payload.carIds = params.carIds;
		if (params.timezone) payload.timezone = params.timezone;

		const response = await createRPCQuery(() =>
			prpc.theirsModel.report.getExecutorOrdersReport(payload),
		);

		if (!response?.items) return null;

		return {
			items: (response.items as any[]).map(
				ExecutorOrdersReport.fromResponse,
			),
			counters: response.counters ?? {
				count: 0,
				orderCount: 0,
				amount: 0,
			},
		};
	}

	static fromResponse(data: any): ExecutorOrdersReport.Item {
		return {
			id: String(data.id),
			executor: {
				id: Number(data.executor?.id ?? 0),
				callSign: data.executor?.callSign ?? "",
				fullName: data.executor?.fullName ?? "",
				taxId: data.executor?.taxId ?? "",
			},
			taxiServiceId: Number(data.taxiServiceId ?? 0),
			registrationNumbers: Array.isArray(data.registrationNumbers)
				? data.registrationNumbers
				: [],
			fleet: data.fleet
				? {
						id: Number(data.fleet.id),
						name: data.fleet.name ?? "",
						idno: data.fleet.idno ?? "",
				  }
				: null,
			company: {
				id: Number(data.company?.id ?? 0),
				platformBrand: data.company?.platformBrand ?? "",
				legalEntityName: data.company?.legalEntityName ?? "",
				legalEntityIdno: data.company?.legalEntityIdno ?? "",
			},
			orderCount: Number(data.orderCount ?? 0),
			amount: Number(data.amount ?? 0),
			currency: data.currency
				? {
						code: data.currency.code ?? null,
						symbol: data.currency.symbol ?? null,
				  }
				: null,
			period: {
				from: new Date(data.period?.from),
				to: new Date(data.period?.to),
			},
		};
	}

	/** «01.08.2026 - 31.08.2026». */
	static formatPeriod(period: ExecutorOrdersReport.Item["period"]): string {
		const dateFns = new DateFns();
		const from = dateFns.formatTime2(period.from, ConstantFormat.MDY_UK);
		const to = dateFns.formatTime2(period.to, ConstantFormat.MDY_UK);
		return `${from} - ${to}`;
	}

	/**
	 * Значение ячейки по id колонки. Одна карта для таблицы на экране
	 * и для xlsx, чтобы файл не отличался от таблицы.
	 */
	static getCellValue(
		item: ExecutorOrdersReport.Item,
		columnId: ExecutorOrdersReport.ColumnId,
	): string | number {
		switch (columnId) {
			case "platformBrand":
				return item.company.platformBrand;
			case "legalEntityName":
				return item.company.legalEntityName;
			case "legalEntityIdno":
				return item.company.legalEntityIdno;
			case "executorFullName":
				return item.executor.fullName || item.executor.callSign;
			case "executorTaxId":
				return item.executor.taxId;
			case "registrationNumbers":
				return item.registrationNumbers.join(", ");
			case "fleetName":
				return item.fleet?.name ?? "";
			case "fleetIdno":
				return item.fleet?.idno ?? "";
			case "accessDocument":
			case "accessValidity":
				return ExecutorOrdersReport.NOT_APPLICABLE;
			case "orderCount":
				return item.orderCount;
			case "amount":
				return item.amount;
			case "period":
				return ExecutorOrdersReport.formatPeriod(item.period);
			default:
				return "";
		}
	}

	/**
	 * xlsx по форме Anexa nr.1 (образец ANTA): шапка в две строки с объединением
	 * по каждой колонке, заголовок формы, нумерация строк и стили как в образце.
	 * Реквизиты приказа сверху и примечания внизу не добавляются.
	 */
	static async exportExcel(
		items: ExecutorOrdersReport.Item[],
		options: ExecutorOrdersReport.ExportOptions,
	): Promise<void> {
		const { FORM_TITLE, FORM_COLUMNS, LAYOUT } = ExecutorOrdersReport;

		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(options.sheetName, {
			pageSetup: { orientation: "portrait" },
		});

		const thin: Partial<ExcelJS.Border> = { style: "thin" };
		const medium: Partial<ExcelJS.Border> = { style: "medium" };

		// Колонка A — узкий отступ, как в образце; данные с B.
		worksheet.getColumn(1).width = LAYOUT.gutterWidth;
		FORM_COLUMNS.forEach((column, index) => {
			worksheet.getColumn(LAYOUT.firstColumn + index).width =
				column.width;
		});

		// Заголовок формы (B6)
		const titleCell = worksheet.getCell(
			LAYOUT.titleRow,
			LAYOUT.firstColumn,
		);
		titleCell.value = FORM_TITLE;
		titleCell.font = { name: "Times New Roman", size: 14, bold: true };
		worksheet.getRow(LAYOUT.titleRow).height = 18.75;

		// Шапка: две строки, каждая колонка объединена по вертикали
		const headerTop = LAYOUT.headerRow;
		const headerBottom = LAYOUT.headerRow + 1;
		const [topHeight, bottomHeight] = LAYOUT.headerHeights;
		worksheet.getRow(headerTop).height = topHeight;
		worksheet.getRow(headerBottom).height = bottomHeight;
		FORM_COLUMNS.forEach((column, index) => {
			const col = LAYOUT.firstColumn + index;
			worksheet.mergeCells(headerTop, col, headerBottom, col);
			const cell = worksheet.getCell(headerTop, col);
			cell.value = column.header;
			cell.font = { name: "Times New Roman", size: 10, bold: true };
			cell.alignment = {
				horizontal: "center",
				vertical: "middle",
				wrapText: true,
			};
			cell.border = {
				top: medium,
				left: medium,
				right: medium,
				bottom: medium,
			};
		});

		// Строки данных: Calibri 11, тонкие рамки, у первой строки верх medium
		items.forEach((item, index) => {
			const row = worksheet.getRow(LAYOUT.firstDataRow + index);
			FORM_COLUMNS.forEach((column, columnIndex) => {
				const cell = row.getCell(LAYOUT.firstColumn + columnIndex);
				cell.value =
					column.id === "rowNumber"
						? index + 1
						: ExecutorOrdersReport.getCellValue(item, column.id);
				cell.font = { name: "Calibri", size: 11 };
				cell.alignment = { vertical: "bottom", wrapText: true };
				cell.border = {
					top: index === 0 ? medium : thin,
					left: thin,
					right: thin,
					bottom: thin,
				};

				if (column.id === "amount") {
					const currency =
						item.currency?.symbol || item.currency?.code || "";
					cell.numFmt = currency
						? `#,##0.00 "${currency}"`
						: "#,##0.00";
				}
				if (column.id === "orderCount" || column.id === "rowNumber") {
					cell.numFmt = "0";
				}
			});
		});

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: "application/octet-stream" });
		saveAs(blob, options.fileName);
	}
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace ExecutorOrdersReport {
	/** Прочерк для колонок, данных по которым в системе нет. */
	export const NOT_APPLICABLE = "-";

	/** Заголовок формы Anexa nr.1 — как в образце, не переводится. */
	export const FORM_TITLE =
		"Structura-tip a informației privind prestarea serviciilor de transport rutier în regim de taxi prin intermediul sistemelor (platformelor) electronice de management";

	/**
	 * Колонки формы Anexa nr.1 — названия и ширины из образца ANTA
	 * (румынский по регламенту, не переводится).
	 */
	export const FORM_COLUMNS: {
		id: ColumnId | "rowNumber";
		header: string;
		width: number;
	}[] = [
		{ id: "rowNumber", header: "Nr. ordine", width: 6.5 },
		{
			id: "platformBrand",
			header: "Marca comercială a sistemului/ platforma electronică",
			width: 12.35,
		},
		{
			id: "legalEntityName",
			header: "Denumirea persoanei juridice deținător a sistemului pe teritoriul RM, date de contact",
			width: 28.8,
		},
		{
			id: "legalEntityIdno",
			header: "Cod fiscal/IDNO deținător a sistemului pe teritoriul RM",
			width: 17.5,
		},
		{
			id: "executorFullName",
			header: "Nume, prenume conducător auto",
			width: 14.35,
		},
		{
			id: "executorTaxId",
			header: "IDNP conducător auto",
			width: 15.67,
		},
		{
			id: "registrationNumbers",
			header: "Număr de înmatriculare unitate de transport",
			width: 13.67,
		},
		{
			id: "fleetName",
			header: "Denumirea operatorului de transport rutier, deținătorul extrasului din ROTR",
			width: 27.17,
		},
		{
			id: "fleetIdno",
			header: "Cod fiscal/IDNO al operatorului de transport rutier, deținătorul extrasului din ROTR",
			width: 28.47,
		},
		{
			id: "accessDocument",
			header: "Nr. și data la actul care confirmă accesul la sistemul/ platforma electronică",
			width: 25.5,
		},
		{
			id: "accessValidity",
			header: "Perioada de valabilitate a accesului solicitat la sistemul/ platforma electronică p/u OTR",
			width: 27.85,
		},
		{
			id: "orderCount",
			header: "Numărul de curse efectuate pentru perioada de referință (separat pentru fiecare lună)",
			width: 14.35,
		},
		{
			id: "amount",
			header: "Suma totală a plăților încasate pentru cursele efectuate",
			width: 15.35,
		},
		{ id: "period", header: "Lună", width: 24.09 },
	];

	/** Раскладка листа — позиции как в образце (заголовок B6, шапка 8–9, данные с 10). */
	export const LAYOUT = {
		gutterWidth: 1.67,
		firstColumn: 2, // B
		titleRow: 6,
		headerRow: 8,
		headerHeights: [52.5, 59.45],
		firstDataRow: 10,
	};

	export type ColumnId =
		| "platformBrand"
		| "legalEntityName"
		| "legalEntityIdno"
		| "executorFullName"
		| "executorTaxId"
		| "registrationNumbers"
		| "fleetName"
		| "fleetIdno"
		| "accessDocument"
		| "accessValidity"
		| "orderCount"
		| "amount"
		| "period";

	export interface SearchOptions {
		dateRange: DateRange;
		taxiServiceIds?: number[];
		fleetIds?: number[];
		executorIds?: number[];
		/** Автомобили (парковые номера), на которых выполнялись заказы. */
		carIds?: number[];
		/** IANA-таймзона браузера для разбивки периода по месяцам. */
		timezone?: string;
	}

	export interface Item {
		id: string;
		executor: {
			id: number;
			callSign: string;
			fullName: string;
			taxId: string;
		};
		taxiServiceId: number;
		registrationNumbers: string[];
		fleet: { id: number; name: string; idno: string } | null;
		company: {
			id: number;
			platformBrand: string;
			legalEntityName: string;
			legalEntityIdno: string;
		};
		orderCount: number;
		amount: number;
		currency: { code: string | null; symbol: string | null } | null;
		period: { from: Date; to: Date };
	}

	export interface Counters {
		count: number;
		orderCount: number;
		amount: number;
	}

	export interface Response {
		items: Item[];
		counters: Counters;
	}

	export interface ExportOptions {
		fileName: string;
		sheetName: string;
	}
}

export default ExecutorOrdersReport;
